/** XeAgents.ai**/
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Info, Loader2, PencilLine, Sparkles } from "lucide-react";
import { StepCard } from "./StepCard";
import type { AIContentState } from "./types";

const PLACEHOLDER = `Introduce the company briefly, explain the product's key value, highlight relevant benefits, handle objections naturally, and ask whether the prospect would like to learn more.

Keep the tone friendly, professional and concise.`;

/**
 * How to run the call, as opposed to what to say on it.
 *
 * Optional, and the one box where somebody says "sell it THIS way" rather than
 * handing over nouns. Facing an empty textarea almost nobody fills it in, so the
 * AI drafts from everything already on the page - but what is drafted here is a
 * suggestion, and an edited box is never overwritten.
 */
export function CallInstructions({
  state,
  onChange,
  onSuggest,
  canSuggest,
  staleForAudience = false,
}: {
  state: AIContentState;
  onChange: (value: string) => void;
  onSuggest: () => void;
  canSuggest: boolean;
  /** The audience changed since these instructions were written, and the person has edited them, so they were left as they are. */
  staleForAudience?: boolean;
}) {
  const generating = state.origin === 'generating';

  return (
    <StepCard
      step={4}
      title="Call Instructions"
      description="Guide the agent on tone, length and what the call is for."
      action={
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onSuggest}
          disabled={!canSuggest || generating}
          data-testid="button-suggest-instructions"
        >
          {generating ? (
            <><Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />Writing</>
          ) : (
            <><Sparkles className="mr-2 h-3.5 w-3.5" />Use AI Suggested Instructions</>
          )}
        </Button>
      }
    >
      <div className="space-y-1.5">
        <div className="flex items-center justify-between gap-2">
          <label htmlFor="call-instructions" className="text-sm font-medium">
            Instructions
            <span className="ml-2 text-xs font-normal text-muted-foreground">Optional</span>
          </label>
          {state.origin === 'ai' && (
            <Badge
              variant="outline"
              className="h-6 gap-1 border-primary/25 bg-primary/5 px-2 text-[11px] font-normal text-primary"
              data-testid="badge-ai-instructions"
            >
              <Sparkles className="h-3 w-3" />
              AI Generated
            </Badge>
          )}
          {state.origin === 'edited' && state.value.trim() && (
            <Badge variant="outline" className="h-6 gap-1 px-2 text-[11px] font-normal text-muted-foreground">
              <PencilLine className="h-3 w-3" />
              Edited by you
            </Badge>
          )}
        </div>

        {generating ? (
          <div className="space-y-2 rounded-md border bg-muted/30 p-3" data-testid="skeleton-instructions">
            <Skeleton className="h-3.5 w-[90%]" />
            <Skeleton className="h-3.5 w-[74%]" />
            <Skeleton className="h-3.5 w-[82%]" />
            <Skeleton className="h-3.5 w-[60%]" />
          </div>
        ) : (
          <Textarea
            id="call-instructions"
            rows={7}
            value={state.value}
            onChange={(event) => onChange(event.target.value)}
            placeholder={PLACEHOLDER}
            className="resize-y text-sm leading-relaxed"
            data-testid="textarea-call-instructions"
          />
        )}

        <p className="text-xs text-muted-foreground leading-snug">
          Written from your product, audience, value proposition, features and objections. Edit or delete anything
          you disagree with.
        </p>

        {staleForAudience && (
          <p className="flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-400" data-testid="note-instructions-stale">
            <Info className="mt-px h-3.5 w-3.5 shrink-0" />
            The audience changed since you edited these. They were left as you wrote them, so the call may still
            lead with the old buyer. Press Use AI Suggested Instructions to redraft them.
          </p>
        )}
      </div>
    </StepCard>
  );
}
